import { createFileRoute } from '@tanstack/react-router';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  FileText,
  Shield,
  Ship,
  Compass,
  AlertTriangle,
  Radio,
  Lock,
  Scale,
  Building2,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { LandingTopBar } from '@/featured/landing-page/components/LandingTopBar';
import { LandingFooter } from '@/featured/landing-page/components/LandingFooter';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    icon: CheckCircle2,
    content: (
      <>
        <p>
          By accessing, registering, or using the <strong>Missi Track</strong> platform, mobile application,
          APIs, or related vessel tracking services (collectively, the &ldquo;Service&rdquo;), provided by{' '}
          <strong>PT Mitos International Solution</strong> (&ldquo;Missi Track&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          or &ldquo;our&rdquo;), you (&ldquo;Customer&rdquo;, &ldquo;User&rdquo;, or &ldquo;you&rdquo;) agree to be bound
          by these Terms and Conditions (&ldquo;Terms&rdquo;).
        </p>
        <p>
          If you are entering into this agreement on behalf of a company, shipping operator, or other legal
          entity, you represent that you have the legal authority to bind that entity to these Terms.
          If you do not agree to these Terms, you must immediately discontinue use of the Service.
        </p>
      </>
    ),
  },
  {
    id: 'services',
    title: '2. Scope of Maritime Services',
    icon: Ship,
    content: (
      <>
        <p>
          Missi Track provides comprehensive maritime telematics and fleet intelligence software solutions,
          including:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li>Real-time vessel GPS tracking and Automatic Identification System (AIS) monitoring.</li>
          <li>Hardware telemetry integration via satellite (Iridium, Globalstar, Orbcomm) and GSM cellular terminals.</li>
          <li>Engine and equipment telemetry monitoring (RPM, Hour Meter, Battery, Fuel Sensor integrations).</li>
          <li>Voyager voyage route planning, waypoints, arrival estimation, and off-track / over-speed alarms.</li>
          <li>Geofencing tools for custom zones, security perimeters, and automated port call detection.</li>
          <li>Historical voyage playback, meteorology weather overlays (wind, waves, currents), and reporting exports.</li>
        </ul>
        <p>
          We continuously update and optimize the Service. We reserve the right to modify, enhance, or
          temporarily suspend specific features with reasonable notice when feasible.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    title: '3. User Accounts and Credentials',
    icon: Lock,
    content: (
      <>
        <p>
          To access the tracking portal, you must maintain an active customer account. You agree to:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li>Provide accurate, current, and complete corporate and contact information.</li>
          <li>Maintain the strict confidentiality of your account credentials, passwords, and API tokens.</li>
          <li>Promptly notify us at <a href="mailto:support@mitos.id" className="text-blue-400 underline">support@mitos.id</a> if you suspect any unauthorized access or breach of security.</li>
          <li>Accept full responsibility for all activities, configurations, and commands executed under your credentials.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'hardware',
    title: '4. Hardware, Satellites & Network Dependencies',
    icon: Radio,
    content: (
      <>
        <p>
          Real-time tracking accuracy depends upon onboard hardware transponders, clear satellite line-of-sight,
          and third-party satellite networks (e.g., Iridium, Globalstar, Orbcomm). The Customer acknowledges:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li>Vessel transponders must be installed, powered, and maintained according to technical specifications.</li>
          <li>Atmospheric conditions, severe weather, satellite outages, or antenna obstructions may cause intermittent telemetry delays or packet loss beyond our direct control.</li>
          <li>Missi Track shall not be held liable for transmission delays caused by third-party satellite network operators or public telecommunication carriers.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'safety',
    title: '5. Navigational Safety Disclaimer',
    icon: AlertTriangle,
    content: (
      <>
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs leading-relaxed mb-3">
          <strong>IMPORTANT NAVIGATIONAL NOTICE:</strong> Missi Track is an advisory and fleet management tool.
          It is <strong>NOT</strong> certified as a primary navigation aid or an electronic chart display and
          information system (ECDIS) under IMO/SOLAS conventions.
        </div>
        <p>
          The Master and crew of each vessel remain solely and legally responsible for the safe navigation,
          seaworthiness, collision avoidance (COLREGS compliance), and operational decisions of the vessel at
          all times. Geofence alarms and weather overlays must not be used as the sole basis for safety-of-life
          decisions at sea.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: '6. Acceptable Use and Restrictions',
    icon: Scale,
    content: (
      <>
        <p>
          You agree to use Missi Track solely for lawful maritime and business purposes. You agree not to:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
          <li>Monitor, track, or intercept vessels for which you lack explicit legal authorization or ownership rights.</li>
          <li>Attempt to reverse-engineer, decompile, or extract source code from the software or hardware protocols.</li>
          <li>Bypass rate limits, security perimeters, or overload the platform infrastructure with automated scripts.</li>
          <li>Use telemetry data to facilitate unlawful acts, smuggling, or maritime sanctions circumvention.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: '7. Intellectual Property Rights',
    icon: Shield,
    content: (
      <>
        <p>
          All software code, user interface designs, logos, databases, algorithms, documentation, and trademarks
          associated with Missi Track are the exclusive intellectual property of PT Mitos International Solution
          or its licensors.
        </p>
        <p>
          Customers retain full ownership of their raw vessel telemetry and business operational data. Customers
          grant Missi Track a worldwide, non-exclusive license to process, store, and display such telemetry
          strictly for providing the Service.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: '8. Limitation of Liability',
    icon: FileText,
    content: (
      <>
        <p>
          To the maximum extent permitted by applicable law, in no event shall PT Mitos International Solution,
          its directors, employees, or partners be liable for any indirect, incidental, special, consequential,
          or punitive damages, including loss of profits, vessel charter delays, cargo damage, collision costs,
          or maritime salvage claims arising out of or in connection with the use or inability to use the Service.
        </p>
      </>
    ),
  },
  {
    id: 'governing-law',
    title: '9. Governing Law & Dispute Resolution',
    icon: Building2,
    content: (
      <>
        <p>
          These Terms are governed by and construed in accordance with the laws of the Republic of Indonesia.
          Any disputes, controversies, or claims arising out of or relating to these Terms shall be resolved
          amicably through mutual discussions. If unresolved, the dispute shall be submitted to the exclusive
          jurisdiction of the District Court of South Jakarta (Pengadilan Negeri Jakarta Selatan).
        </p>
      </>
    ),
  },
];

export default function TermsPage() {
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'ltr');
    document.body.style.backgroundColor = '#070e1b';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <>
      <Head>
        <title>Terms & Conditions — Missi Track</title>
        <meta
          name="description"
          content="Terms and Conditions of Service for Missi Track maritime fleet tracking and telemetry management platform."
        />
        <meta property="og:title" content="Terms & Conditions — Missi Track" />
        <meta
          property="og:description"
          content="Official Terms of Service for Missi Track maritime vessel management and telematics platform."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Head>

      <main
        className="landing min-h-screen text-slate-200 overflow-x-hidden relative"
        style={{ backgroundColor: '#070e1b' }}
      >
        <LandingTopBar />

        {/* Hero Header */}
        <section className="relative -mt-24 pt-36 pb-16 px-4 overflow-hidden border-b border-slate-800 bg-[#070e1b]">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-blue-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-4xl mx-auto text-center space-y-4">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Legal & Service Agreement</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight"
            >
              Terms & Conditions
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto"
            >
              Please read these terms carefully before using Missi Track vessel tracking and maritime telemetry platform.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="text-xs text-slate-500 pt-2"
            >
              Last Updated: September 19, 2026 • Effective Date: January 1, 2026
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {SECTIONS.map((sec) => {
              const Icon = sec.icon;
              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="p-6 sm:p-8 rounded-2xl bg-slate-900/60 border border-slate-800 shadow-sm space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">{sec.title}</h2>
                  </div>
                  <div className="text-sm leading-relaxed text-slate-300 space-y-3 pl-0 sm:pl-13">
                    {sec.content}
                  </div>
                </div>
              );
            })}

            {/* Contact Card */}
            <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-blue-950/40 to-slate-900/80 border border-blue-800/40 space-y-4">
              <h3 className="text-base sm:text-lg font-bold text-white">Questions About These Terms?</h3>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                If you have questions regarding these Terms & Conditions or need technical clarifications regarding our
                maritime service agreement, please contact our team:
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2 text-xs">
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
                  <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                  <div>
                    <span className="text-slate-400 block text-[10px]">Email Support</span>
                    <a href="mailto:support@mitos.id" className="text-white font-medium hover:text-blue-400">
                      support@mitos.id
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-800/60 border border-slate-700/50">
                  <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                  <div>
                    <span className="text-slate-400 block text-[10px]">Phone</span>
                    <a href="tel:+622127874648" className="text-white font-medium hover:text-emerald-400">
                      021 2787 4648
                    </a>
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-400 border-t border-slate-800/80 pt-3">
                <span className="font-semibold text-slate-300">Office Address: </span>
                Perkantoran Tanjung Mas Raya, blok B1 nomor 44, RT.002/RW.001. Tanjung Barat, Jagakarsa, Jakarta Selatan.
              </div>
            </div>

            {/* Link to Privacy Policy */}
            <div className="text-center pt-4">
              <Link
                href="/privacy"
                className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                <span>Read our Privacy Policy</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <LandingFooter />
      </main>
    </>
  );
}

TermsPage.guestGuard = false;
TermsPage.authGuard = false;

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});
